import React, { useEffect, useState } from 'react';
import { Box, Tabs, Tab, Typography, CircularProgress, Divider } from '@material-ui/core';
import { Accordion, AccordionSummary, AccordionDetails } from '@material-ui/core';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Alert from "@material-ui/lab/Alert";
import { useSelector } from 'react-redux';

export interface LintingMetricsProps {
    specLintResultUrl: string;
    specFile?: string;
    lintingStatus?: string;
}

const LintingMetrics: React.FC<LintingMetricsProps> = ({  specLintResultUrl, specFile, lintingStatus }) => {
    const [apiLintingData, setApiLintingData] = useState<any>([]);
    const identityEnvironment = useSelector((state: any) => state?.common?.identityEnvironment ?? "trimble-pre-prod");
    const [activeSeverityTab, setActiveSeverityTab] = useState('0'); 
    const [loading, setLoading] = useState(true);
    const [specFileName, setSpecFileName] = useState("");
    const [lastRunTimestamp, setLastRunTimestamp] = useState("");

      
    useEffect(() => {

        const fetchLintingData = async () => {
            try {
                if (specLintResultUrl !== "") {
                    const response = await fetch(`${specLintResultUrl}`);
                    if (!response.ok) throw new Error("Failed to fetch linting results");
                    const textData = await response.json();
                    setApiLintingData(textData);
                    
                    // Extract timestamp from the URL filename - handle signed URLs properly
                    const urlPath = specLintResultUrl?.split('?')[0];
                    const filenameMatch = urlPath?.match(/[^/\\]+$/);
                    if (filenameMatch && filenameMatch.length > 0) {
                        const filename = filenameMatch[0];
                        setSpecFileName(filename);
                        
                        const timestampMatch = filename.match(/[-](\d+)_/);
                        if (timestampMatch && timestampMatch.length > 1) {
                            const timestamp = parseInt(timestampMatch[1]);
                            const date = new Date(timestamp);
                            if (isNaN(date.getTime())) {
                                throw new Error('Invalid timestamp');
                            }
                            // Format: YYYY-MM-DD HH:MM:SS UTC
                            const formattedDate = 
                                date.getUTCFullYear() + '-' + 
                                String(date.getUTCMonth() + 1).padStart(2, '0') + '-' + 
                                String(date.getUTCDate()).padStart(2, '0') + ' ' + 
                                String(date.getUTCHours()).padStart(2, '0') + ':' + 
                                String(date.getUTCMinutes()).padStart(2, '0') + ':' + 
                                String(date.getUTCSeconds()).padStart(2, '0') + ' UTC';
                            setLastRunTimestamp(formattedDate);
                        }
                    }
                }
            } catch (err) {
                setApiLintingData([]);
            } finally {
                setLoading(false);
            }
        };

        fetchLintingData();
    }, [identityEnvironment, specLintResultUrl]);

    const groupByCode = (data: any[]) => {
        return data.reduce((acc: any, item: any) => {
            if (!acc[item.code]) {
                acc[item.code] = [];
            }
            acc[item.code].push(item);
            return acc;
        }, {});
    };

    const filterBySeverity = (severity: number) => {
        return apiLintingData.filter((item: any) => item.severity === severity);
    };

    if (loading) return <div>Loading...</div>;

    const severityData: Record<'Errors' | 'Warnings' | 'Hints' | 'Info', any[]> = {
        Errors: filterBySeverity(0),
        Warnings: filterBySeverity(1),
        Hints: filterBySeverity(2),
        Info: filterBySeverity(3),
    };

    const groupedData = groupByCode(severityData[Object.keys(severityData)[parseInt(activeSeverityTab)] as keyof typeof severityData]);
    const productTeamEmail = process.env.REACT_APP_CONSOLE_PRODUCT_EMAIL;

    return (
                <Box>
                    {apiLintingData.length > 0 ? (
                        <>
                                <Alert variant="outlined" severity="info">
                                <Box p={2} borderRadius={4}>
                                    <Typography variant="body2" color="textSecondary">
                                        The linting data below is based on the Trimble HTTP API Standard <a href="https://developer.trimble.com/docs/api-standard/specification/r2023-1/overview" target="_blank" rel="noopener noreferrer">R2023.1</a>. It will be updated with R2026.1 and the option to indicate which version to use in the near future.
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                                        The data is intended to provide an understanding of what it might take to adopt the standard, either with no breaking changes or in a new major version of the linted API.
                                    </Typography>
                                    <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                                        Email <a href={`mailto:${productTeamEmail}`}>{productTeamEmail}</a> with any questions or feedback!
                                    </Typography>
                                </Box>
                                </Alert>
                                <Box display="flex" justifyContent="space-between" mb={2} mt={2}>
                                <Typography variant="body2" color="textSecondary">
                                    Last run: {lastRunTimestamp || specFileName}
                                </Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Linted version: R2023.1
                                </Typography>
                            </Box>
                            <Tabs
                                value={activeSeverityTab}
                                onChange={(event, newValue) => setActiveSeverityTab(newValue)}
                                indicatorColor="primary"
                                textColor="primary"
                                style={{ marginBottom: '16px' }}
                            >
                                <Tab label={`Errors (${severityData.Errors.length})`} value="0" />
                                <Tab label={`Warnings (${severityData.Warnings.length})`} value="1" />
                                <Tab label={`Hints (${severityData.Hints.length})`} value="2" />
                                <Tab label={`Info (${severityData.Info.length})`} value="3" />
                            </Tabs>
                            <Box>
                                {Object.keys(groupedData).map((code, index) => (
                                    <Accordion key={index}>
                                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                            <Typography variant="subtitle1">
                                                {code} ({groupedData[code].length})
                                            </Typography>
                                        </AccordionSummary>
                                        <AccordionDetails>
                                            <Box>
                                                {groupedData[code][0]?.documentationUrl && (
                                                    <Typography style={{ marginBottom: '12px'}}>
                                                        Documentation: <a href={groupedData[code][0].documentationUrl} target="_blank" rel="noopener noreferrer">{groupedData[code][0].documentationUrl}</a>
                                                    </Typography>
                                                )}
                                                {groupedData[code].map((item: any, idx: number) => (
                                                    <Typography key={idx} variant="body2" style={{ marginBottom: '8px' }}>
                                                        Message: {item.message} <br />
                                                        Path: {item.path.join(' > ')} <br />
                                                        Start Line: {item.range.start.line}, End Line: {item.range.end.line}
                                                    </Typography>
                                                ))}
                                            </Box>
                                        </AccordionDetails>
                                    </Accordion>
                                ))}
                            </Box>
                        </>
                    ) : (
                        <Typography variant="body1">{ specFile ? lintingStatus !=="IN_PROGRESS" ? "Linting metrics are unable to process, please re-upload again" : "Linting results are processing, please check back later." : "No linting results available."}</Typography>
                    )}
                </Box>
            
    );
};

export default LintingMetrics;
